import React, { useState } from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  padding: 24px;
  background: #f5f7fa;
  min-height: 100vh;
`;

const PageHeader = styled.header`
  background: white;
  padding: 24px 32px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  margin-bottom: 24px;
  border-left: 4px solid #667eea;
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 4px;
`;

const PageSubtitle = styled.p`
  color: #718096;
  font-size: 0.875rem;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;
`;

const SettingsCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 20px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  
  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1a202c;
    margin: 0;
  }
  
  i {
    color: #667eea;
    font-size: 1rem;
  }
`;

const CardContent = styled.div`
  padding: 24px;
`;

const SettingItem = styled.div`
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SettingLabel = styled.label`
  display: block;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
  font-size: 0.875rem;
`;

const SettingInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const SettingSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const SettingToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
`;

const ToggleSwitch = styled.div`
  position: relative;
  width: 44px;
  height: 24px;
  background: ${props => props.checked ? '#667eea' : '#d1d5db'};
  border-radius: 12px;
  transition: background 0.2s ease;
  
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.checked ? '22px' : '2px'};
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: left 0.2s ease;
  }
`;

const SaveButton = styled.button`
  background: #667eea;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: #5a67d8;
  }
`;

const Settings = () => {
  const [settings, setSettings] = useState({
    streamQuality: '1080p',
    maxBitrate: '2500',
    autoStart: true,
    notifications: true,
    recording: false,
    serverPort: '1935',
    hlsPort: '8000'
  });

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = () => {
    // Here you would typically save settings to backend
    console.log('Saving settings:', settings);
    alert('Settings saved successfully!');
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Settings</PageTitle>
        <PageSubtitle>Configure your streaming preferences and system settings</PageSubtitle>
      </PageHeader>

      <SettingsGrid>
        <SettingsCard>
          <CardHeader>
            <i className="fas fa-video"></i>
            <h3>Stream Settings</h3>
          </CardHeader>
          <CardContent>
            <SettingItem>
              <SettingLabel>Stream Quality</SettingLabel>
              <SettingSelect
                value={settings.streamQuality}
                onChange={(e) => handleSettingChange('streamQuality', e.target.value)}
              >
                <option value="720p">720p</option>
                <option value="1080p">1080p</option>
                <option value="4K">4K</option>
              </SettingSelect>
            </SettingItem>
            
            <SettingItem>
              <SettingLabel>Max Bitrate (kbps)</SettingLabel>
              <SettingInput
                type="number"
                value={settings.maxBitrate}
                onChange={(e) => handleSettingChange('maxBitrate', e.target.value)}
                placeholder="2500"
              />
            </SettingItem>
            
            <SettingItem>
              <SettingToggle>
                <ToggleSwitch checked={settings.autoStart} />
                <span>Auto-start streams</span>
              </SettingToggle>
            </SettingItem>
            
            <SettingItem>
              <SettingToggle>
                <ToggleSwitch checked={settings.recording} />
                <span>Auto-record streams</span>
              </SettingToggle>
            </SettingItem>
          </CardContent>
        </SettingsCard>

        <SettingsCard>
          <CardHeader>
            <i className="fas fa-server"></i>
            <h3>Server Settings</h3>
          </CardHeader>
          <CardContent>
            <SettingItem>
              <SettingLabel>RTMP Port</SettingLabel>
              <SettingInput
                type="number"
                value={settings.serverPort}
                onChange={(e) => handleSettingChange('serverPort', e.target.value)}
                placeholder="1935"
              />
            </SettingItem>
            
            <SettingItem>
              <SettingLabel>HLS Port</SettingLabel>
              <SettingInput
                type="number"
                value={settings.hlsPort}
                onChange={(e) => handleSettingChange('hlsPort', e.target.value)}
                placeholder="8000"
              />
            </SettingItem>
            
            <SettingItem>
              <SettingToggle>
                <ToggleSwitch checked={settings.notifications} />
                <span>Enable notifications</span>
              </SettingToggle>
            </SettingItem>
          </CardContent>
        </SettingsCard>

        <SettingsCard>
          <CardHeader>
            <i className="fas fa-shield-alt"></i>
            <h3>Security Settings</h3>
          </CardHeader>
          <CardContent>
            <SettingItem>
              <SettingLabel>API Key</SettingLabel>
              <SettingInput
                type="password"
                value="holytv_api_key_2024_secure_streaming_server"
                readOnly
              />
            </SettingItem>
            
            <SettingItem>
              <SettingLabel>Stream Key</SettingLabel>
              <SettingInput
                type="password"
                value="holy_tv_livestream_key"
                readOnly
              />
            </SettingItem>
            
            <SaveButton onClick={handleSave}>
              <i className="fas fa-save"></i>
              Save Settings
            </SaveButton>
          </CardContent>
        </SettingsCard>
      </SettingsGrid>
    </PageContainer>
  );
};

export default Settings;
